import { ContractRevenueItem } from "@/pages/contracts/types";
import { BillingFrequency } from "@/pages/settings/pages/products-v2/types";
import { recordedPeriodInMonths } from "@/pages/deals/components/commercials/DealCommercialsV2/ContractTermsV2/consts";
import { DraftRevenueItem } from "@/pages/contracts/components/termsDraft/types";

export interface ContractFinancialEstimate {
  mrr: number;
  cmrr: number;
  acv: number;
  tcv: number;
}

const billsOnce = (item: ContractRevenueItem): boolean =>
  item.customBillingFrequency === BillingFrequency.ONCE;

// A line with no start date is not scheduled, so there is no monthly rate to
// project from it — but it still runs for whatever term it records, which is a
// separate question from how often it bills.
const isOneTime = (item: ContractRevenueItem): boolean =>
  billsOnce(item) || !item.serviceStartDate;

// Reading the duration enum first means a custom term — which carries no month
// count there — resolves to a single month and inflates every monthly figure
// derived from it. The shared resolution reads the dates where the line has
// them, which is what states a custom term's length.
// A charge billed once covers a single period whatever span its dates records,
// so it has to say so here too: costing it across the whole engagement is what
// turns a fee billed once into a fraction of itself.
const getDurationInMonths = (item: ContractRevenueItem): number =>
  recordedPeriodInMonths(item, null, billsOnce(item));

// Local approximation of the backend's calculated financials, applied
// identically to the source and the draft so the deltas shown in the
// Change column stay consistent. The created contract's real values are
// computed server-side.
export const estimateFinancials = (
  items: DraftRevenueItem[]
): ContractFinancialEstimate => {
  let mrr = 0;
  let cmrr = 0;
  let acv = 0;
  let tcv = 0;

  items
    .filter((item) => !item.draftDeleted)
    .forEach((item) => {
      const total = item.totalLocal ?? 0;
      const months = getDurationInMonths(item);
      tcv += total;
      acv += months <= 12 ? total : (total * 12) / months;
      if (!isOneTime(item)) {
        const monthly = total / months;
        mrr += monthly;
        if (!item.customOptOutDate) {
          cmrr += monthly;
        }
      }
    });

  return { mrr, cmrr, acv, tcv };
};

export const financialDeltas = (
  draft: ContractFinancialEstimate,
  source: ContractFinancialEstimate
): ContractFinancialEstimate => ({
  mrr: draft.mrr - source.mrr,
  cmrr: draft.cmrr - source.cmrr,
  acv: draft.acv - source.acv,
  tcv: draft.tcv - source.tcv
});
